import httpClient from "@utils/httpClient";

export interface FeedPost {
  id: number;
  title: string;
  content: string;
  category: 'announcement' | 'policy_update' | 'general_notice';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  author_id: string;
  author_name: string;
  is_pinned: boolean;
  requires_acknowledgment: boolean;
  created_at: string;
  updated_at: string;
  acknowledgment_count: number;
  total_employees: number;
  user_acknowledged: boolean;
}

export interface CreatePostData {
  title: string;
  content: string;
  category?: 'announcement' | 'policy_update' | 'general_notice';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  is_pinned?: boolean;
  requires_acknowledgment?: boolean;
}

export interface UpdatePostData {
  title?: string;
  content?: string;
  category?: 'announcement' | 'policy_update' | 'general_notice';
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  is_pinned?: boolean;
  requires_acknowledgment?: boolean;
}

export interface Pagination {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AcknowledgmentUser {
  id: number;
  user_id: string;
  user_name: string;
  user_email: string;
  acknowledged_at?: string;
}

export interface AcknowledgmentData {
  acknowledged: {
    users: AcknowledgmentUser[];
    count: number;
  };
  unacknowledged: {
    users: AcknowledgmentUser[];
    count: number;
  };
  pagination: Pagination;
}

/**
 * Get all feed posts with pagination
 */
export const getFeedPosts = async (page = 1, limit = 10, category?: string) => {
  try {
    let url = `/organization-feed?page=${page}&limit=${limit}`;
    if (category) {
      url += `&category=${category}`;
    }

    const response = await httpClient.get(url);

    return {
      success: response.success,
      data: response.data || [],
      pagination: response.pagination || null,
      error: response.error
    };
  } catch (error: any) {
    console.error('getFeedPosts error:', error);
    return {
      success: false,
      data: [],
      pagination: null,
      error: { message: error.message }
    };
  }
};

/**
 * Get a single post by ID
 */
export const getPostById = async (id: number) => {
  try {
    const response = await httpClient.get(`/organization-feed/${id}`);

    return {
      success: response.success,
      data: response.data || null,
      error: response.error
    };
  } catch (error: any) {
    console.error('getPostById error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message }
    };
  }
};

/**
 * Get recent posts for dashboard widget
 */
export const getRecentPosts = async (limit = 3) => {
  try {
    const response = await httpClient.get(`/organization-feed/recent?limit=${limit}`);

    return {
      success: response.success,
      data: response.data || [],
      error: response.error
    };
  } catch (error: any) {
    console.error('getRecentPosts error:', error);
    return {
      success: false,
      data: [],
      error: { message: error.message }
    };
  }
};

/**
 * Create a new feed post
 */
export const createPost = async (postData: CreatePostData) => {
  try {
    const response = await httpClient.post('/organization-feed', postData);

    return {
      success: response.success,
      data: response.data || null,
      error: response.error
    };
  } catch (error: any) {
    console.error('createPost error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to create post' }
    };
  }
};

/**
 * Update an existing post
 */
export const updatePost = async (id: number, postData: UpdatePostData) => {
  try {
    const response = await httpClient.put(`/organization-feed/${id}`, postData);

    return {
      success: response.success,
      data: response.data || null,
      error: response.error
    };
  } catch (error: any) {
    console.error('updatePost error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to update post' }
    };
  }
};

/**
 * Delete a post (soft delete)
 */
export const deletePost = async (id: number) => {
  try {
    const response = await httpClient.delete(`/organization-feed/${id}`);

    return {
      success: response.success,
      data: response.data || null,
      error: response.error
    };
  } catch (error: any) {
    console.error('deletePost error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to delete post' }
    };
  }
};

/**
 * Acknowledge a post (read receipt)
 */
export const acknowledgePost = async (id: number) => {
  try {
    const response = await httpClient.post(`/organization-feed/${id}/acknowledge`);

    return {
      success: response.success,
      data: response.data || null,
      error: response.error
    };
  } catch (error: any) {
    console.error('acknowledgePost error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message || 'Failed to acknowledge post' }
    };
  }
};

/**
 * Get acknowledgment list for a post (HR/Admin only)
 */
export const getAcknowledgments = async (id: number, page = 1, limit = 20) => {
  try {
    const response = await httpClient.get(`/organization-feed/${id}/acknowledgments?page=${page}&limit=${limit}`);

    return {
      success: response.success,
      data: response.data || null,
      error: response.error
    };
  } catch (error: any) {
    console.error('getAcknowledgments error:', error);
    return {
      success: false,
      data: null,
      error: { message: error.message }
    };
  }
};
